import { memo, useEffect, useMemo, useRef, useState } from "react";
import QuickPromptChipGroup from "../ui/QuickPromptChipGroup";
import RichTextEditor from "../ui/RichTextEditor";
import { hasRichTextContent, richTextToPlainText } from "../../utils/richTextHelpers";
import { splitPromptsByTarget } from "../../utils/quickPromptHelpers";

function EditorTab({
  c,
  dark,
  lbl,
  inp,
  fields,
  modOpen,
  regionOpen,
  geschlechtOpen,
  setModOpen,
  setRegionOpen,
  setGeschlechtOpen,
  showStatus,
  selectModality,
  selectRegion,
  selectGeschlecht,
  vorlagenForMod,
  regionOptionsForMod,
  regionTreeForMod,
  selectedRegionLabel,
  modColor,
  copyText,
  undoField,
  redoField,
  fieldHistory,
  fieldFuture,
  updateField,
  updateKlinischeAngaben,
  updateFragestellung,
  aiQuickPrompts,
  aiBeurteilungQuickPrompts,
  aiPromptSyncStatus,
  canDeleteAiPrompts,
  saveAiQuickPrompt,
  saveAiBeurteilungQuickPrompt,
  deleteAiQuickPrompt,
  deleteAiBeurteilungQuickPrompt,
  aiBefundPrompt,
  aiBeurteilungPrompt,
  beurteilungGeneratePrompt,
  beurteilungStyleProfile,
  setAiBefundPrompt,
  setAiBeurteilungPrompt,
  setBeurteilungGeneratePrompt,
  setBeurteilungStyleProfile,
  onSaveBeurteilungGeneratePrompt,
  aiBefundResponse,
  aiBefundHistory,
  aiBefundLastAction,
  aiBefundSelection,
  updateAiBefundSelection,
  isAiBefundLoading,
  isAiBeurteilungLoading,
  runAiBefundAssistant,
  runAiBeurteilungAssistant,
  applyAiBefundResponse,
  restoreAiBefundHistoryEntry,
  deleteAiBefundHistoryEntry,
  clearAiBefundHistory,
  Btn,
  DropItem,
  InlineDropdown,
  isGenerating,
  generateBeurteilung,
  currentModGroup,
  tplName,
  setTplName,
  tplSection,
  setTplSection,
  availableSections,
  confirmSaveTemplate,
  isSavingTemplate,
  editingId,
  setEditingId,
  resetApp,
  modalities,
  modalityTree,
  lastAutosaveAt,
  reportHistory,
  saveCurrentToHistory,
  restoreFromHistory,
  deleteHistoryEntry,
  clearHistory,
}) {
  const [modDropdownGroup, setModDropdownGroup] = useState("");
  const [regionDropdownSection, setRegionDropdownSection] = useState("");
  const [regionDropdownCategory, setRegionDropdownCategory] = useState("");

  useEffect(() => {
    if (regionOpen) {
      if (fields.region && regionTreeForMod) {
        const tree = regionTreeForMod(fields.modalitat);
        const currentSection = tree.sections.find((s) =>
          s.items.some((it) => it.value === fields.region)
        );
        if (currentSection) {
          setRegionDropdownSection(currentSection.name);
          const currentCategory = currentSection.categories?.find((category) =>
            category.items.some((it) => it.value === fields.region)
          );
          setRegionDropdownCategory(currentSection.hasNestedCategories ? currentCategory?.name || "" : "");
          return;
        }
      }
      setRegionDropdownSection("");
      setRegionDropdownCategory("");
    } else {
      setRegionDropdownSection("");
      setRegionDropdownCategory("");
    }
  }, [regionOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modOpen) {
      if (fields.modalitat && modalityTree) {
        const group = fields.modalitat.split(" ")[0];
        const node = modalityTree.find((n) => n.group === group);
        if (node && !node.single) {
          setModDropdownGroup(group);
          return;
        }
      }
      setModDropdownGroup("");
    } else {
      setModDropdownGroup("");
    }
  }, [modOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const QUICK_PROMPT_DELETE_CONFIRM_TEXT = "Da li ste sigurni da želite obrisati ovaj AI prompt?";
  const QUICK_PROMPT_SELECTION_HINT = "Hinweis: Wenn im Befund Text markiert ist, wird nur die Auswahl überarbeitet.";
  const [quickPromptEditorOpen, setQuickPromptEditorOpen] = useState(false);
  const [editingQuickPromptId, setEditingQuickPromptId] = useState(null);
  const [quickPromptTitleInput, setQuickPromptTitleInput] = useState("");
  const [quickPromptTextInput, setQuickPromptTextInput] = useState("");
  const [quickPromptTargetInput, setQuickPromptTargetInput] = useState("report");
  const [beurteilungQuickPromptEditorOpen, setBeurteilungQuickPromptEditorOpen] = useState(false);
  const [editingBeurteilungQuickPromptId, setEditingBeurteilungQuickPromptId] = useState(null);
  const [beurteilungQuickPromptTitleInput, setBeurteilungQuickPromptTitleInput] = useState("");
  const [beurteilungQuickPromptTextInput, setBeurteilungQuickPromptTextInput] = useState("");
  const [beurteilungQuickPromptTargetInput, setBeurteilungQuickPromptTargetInput] = useState("report");
  const [showGeneratePromptEditor, setShowGeneratePromptEditor] = useState(false);
  const [dataWarningPopup, setDataWarningPopup] = useState({
    visible: false,
    top: 84,
    left: null,
  });
  const klinischeTextareaRef = useRef(null);
  const fragestellungTextareaRef = useRef(null);
  const dataWarningTimeoutRef = useRef(null);
  const regionOptions = useMemo(
    () => regionOptionsForMod(fields.modalitat),
    [fields.modalitat, regionOptionsForMod]
  );
  const regionTree = useMemo(
    () => (regionTreeForMod ? regionTreeForMod(fields.modalitat) : null),
    [fields.modalitat, regionTreeForMod]
  );
  const selectedRegionOption = useMemo(
    () => regionOptions.find((option) => option.value === fields.region) || null,
    [fields.region, regionOptions]
  );
  const regionTriggerValue = selectedRegionLabel || selectedRegionOption?.label || fields.region;
  const selectedRegionDisplayName = selectedRegionOption?.displayName || selectedRegionLabel || fields.region || "";

  const autoResizeTextarea = (element, maxRows = 5) => {
    if (!element) return;
    const styles = window.getComputedStyle(element);
    const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
    const paddingTop = Number.parseFloat(styles.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(styles.paddingBottom) || 0;
    const borderTop = Number.parseFloat(styles.borderTopWidth) || 0;
    const borderBottom = Number.parseFloat(styles.borderBottomWidth) || 0;
    const maxHeight = lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom;

    // Use visibility:hidden clone to measure scrollHeight without causing visible reflow.
    const clone = element.cloneNode(true);
    clone.style.position = "absolute";
    clone.style.top = "-9999px";
    clone.style.left = "-9999px";
    clone.style.width = `${element.offsetWidth}px`;
    clone.style.height = "auto";
    clone.style.visibility = "hidden";
    clone.style.overflow = "hidden";
    document.body.appendChild(clone);
    const scrollH = clone.scrollHeight;
    document.body.removeChild(clone);

    const targetHeight = Math.min(scrollH, maxHeight);
    const newHeight = `${targetHeight}px`;
    if (element.style.height !== newHeight) {
      element.style.height = newHeight;
    }
    element.style.overflowY = scrollH > maxHeight ? "auto" : "hidden";
  };

  const resetQuickPromptEditor = () => {
    setEditingQuickPromptId(null);
    setQuickPromptTitleInput("");
    setQuickPromptTextInput("");
    setQuickPromptTargetInput("report");
    setQuickPromptEditorOpen(false);
  };

  const resetBeurteilungQuickPromptEditor = () => {
    setEditingBeurteilungQuickPromptId(null);
    setBeurteilungQuickPromptTitleInput("");
    setBeurteilungQuickPromptTextInput("");
    setBeurteilungQuickPromptTargetInput("report");
    setBeurteilungQuickPromptEditorOpen(false);
  };

  useEffect(() => {
    autoResizeTextarea(klinischeTextareaRef.current, 5);
  }, [fields.klinischeAngaben]);

  useEffect(() => {
    autoResizeTextarea(fragestellungTextareaRef.current, 5);
  }, [fields.fragestellung]);

  useEffect(() => () => {
    if (dataWarningTimeoutRef.current) {
      clearTimeout(dataWarningTimeoutRef.current);
    }
  }, []);

  const triggerDataWarning = (targetElement) => {
    if (dataWarningTimeoutRef.current) {
      clearTimeout(dataWarningTimeoutRef.current);
    }

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const centerX = rect.left + rect.width / 2;
      const clampedLeft = Math.min(Math.max(centerX, 120), Math.max(viewportWidth - 120, 120));
      const top = Math.max(rect.top - 8, 16);
      setDataWarningPopup({ visible: true, top, left: clampedLeft });
    } else {
      setDataWarningPopup((prev) => ({ ...prev, visible: true }));
    }

    dataWarningTimeoutRef.current = setTimeout(() => {
      setDataWarningPopup((prev) => ({ ...prev, visible: false }));
      dataWarningTimeoutRef.current = null;
    }, 3000);
  };

  const handlePasteNormalizeDelimiters = (e, updateFn, currentValue) => {
    const raw = e.clipboardData?.getData("text") ?? "";
    if (!raw.includes("|")) return;
    e.preventDefault();
    const normalized = raw.replace(/ \| /g, "\n").replace(/\|/g, "\n");
    const el = e.currentTarget;
    const start = el.selectionStart ?? currentValue.length;
    const end = el.selectionEnd ?? currentValue.length;
    const next = currentValue.slice(0, start) + normalized + currentValue.slice(end);
    updateFn(next);
  };

  const handleEditorFocusCapture = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const isDataField =
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.dataset.entryField === "true";
    if (!isDataField) return;
    triggerDataWarning(target);
  };

  const startQuickPromptEdit = (prompt) => {
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu bearbeiten.", true);
      return;
    }
    setQuickPromptEditorOpen(true);
    setEditingQuickPromptId(prompt?.id || null);
    setQuickPromptTitleInput(prompt?.title || "");
    setQuickPromptTextInput(prompt?.text || "");
    setQuickPromptTargetInput(prompt?.target === "assistant" ? "assistant" : "report");
  };

  const startQuickPromptCreate = () => {
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu bearbeiten.", true);
      return;
    }
    setQuickPromptEditorOpen(true);
    setEditingQuickPromptId(null);
    setQuickPromptTitleInput("");
    setQuickPromptTextInput("");
    setQuickPromptTargetInput("report");
  };

  const handleQuickPromptSave = async () => {
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu bearbeiten.", true);
      return;
    }
    const saved = await saveAiQuickPrompt({
      id: editingQuickPromptId,
      title: quickPromptTitleInput,
      text: quickPromptTextInput,
      target: quickPromptTargetInput,
    });
    if (!saved) return;
    resetQuickPromptEditor();
  };

  const handleQuickPromptDelete = async () => {
    if (!editingQuickPromptId) return;
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu löschen.", true);
      return;
    }
    if (!window.confirm(QUICK_PROMPT_DELETE_CONFIRM_TEXT)) return;
    await deleteAiQuickPrompt(editingQuickPromptId);
    resetQuickPromptEditor();
  };

  const handleQuickPromptDeleteFromChip = async (id) => {
    if (!id) return;
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu löschen.", true);
      return;
    }
    if (!window.confirm(QUICK_PROMPT_DELETE_CONFIRM_TEXT)) return;
    await deleteAiQuickPrompt(id);
  };

  const handleQuickPromptApply = (prompt) => {
    const promptText = prompt?.text;
    const nextPrompt = String(promptText || "").trim();
    if (!nextPrompt) return;
    const promptTarget = prompt?.target === "assistant" ? "assistant" : "report";
    const hasBefund = !!fields.befund?.trim();

    if (promptTarget === "assistant") {
      const basePrompt = String(aiBefundPrompt || "").trim();
      const combinedPrompt = basePrompt ? `${basePrompt}\n\n${nextPrompt}` : nextPrompt;
      setAiBefundPrompt(combinedPrompt);
      if (!hasBefund) {
        showStatus("Prompt gesetzt. Bitte zuerst einen Befund eingeben, dann AI-Assistent starten.", true);
        return;
      }
      const hasSelection = !!aiBefundSelection.text?.trim();
      runAiBefundAssistant(hasSelection ? "selection" : "full", combinedPrompt);
      return;
    }

    setAiBefundPrompt(nextPrompt);
    if (!hasBefund) {
      showStatus("Prompt gesetzt. Bitte zuerst einen Befund eingeben, dann AI-Assistent starten.", true);
      return;
    }
    const hasSelection = !!aiBefundSelection.text?.trim();
    runAiBefundAssistant(hasSelection ? "selection" : "full", nextPrompt);
  };

  const startBeurteilungQuickPromptEdit = (prompt) => {
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu bearbeiten.", true);
      return;
    }
    setBeurteilungQuickPromptEditorOpen(true);
    setEditingBeurteilungQuickPromptId(prompt?.id || null);
    setBeurteilungQuickPromptTitleInput(prompt?.title || "");
    setBeurteilungQuickPromptTextInput(prompt?.text || "");
    setBeurteilungQuickPromptTargetInput(prompt?.target === "assistant" ? "assistant" : "report");
  };

  const startBeurteilungQuickPromptCreate = () => {
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu bearbeiten.", true);
      return;
    }
    setBeurteilungQuickPromptEditorOpen(true);
    setEditingBeurteilungQuickPromptId(null);
    setBeurteilungQuickPromptTitleInput("");
    setBeurteilungQuickPromptTextInput("");
    setBeurteilungQuickPromptTargetInput("report");
  };

  const handleBeurteilungQuickPromptSave = async () => {
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu bearbeiten.", true);
      return;
    }
    const saved = await saveAiBeurteilungQuickPrompt({
      id: editingBeurteilungQuickPromptId,
      title: beurteilungQuickPromptTitleInput,
      text: beurteilungQuickPromptTextInput,
      target: beurteilungQuickPromptTargetInput,
    });
    if (!saved) return;
    resetBeurteilungQuickPromptEditor();
  };

  const handleBeurteilungQuickPromptDelete = async () => {
    if (!editingBeurteilungQuickPromptId) return;
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu löschen.", true);
      return;
    }
    if (!window.confirm(QUICK_PROMPT_DELETE_CONFIRM_TEXT)) return;
    await deleteAiBeurteilungQuickPrompt(editingBeurteilungQuickPromptId);
    resetBeurteilungQuickPromptEditor();
  };

  const handleBeurteilungQuickPromptDeleteFromChip = async (id) => {
    if (!id) return;
    if (!canDeleteAiPrompts) {
      showStatus("Bitte anmelden, um AI-Prompts zu löschen.", true);
      return;
    }
    if (!window.confirm(QUICK_PROMPT_DELETE_CONFIRM_TEXT)) return;
    await deleteAiBeurteilungQuickPrompt(id);
  };

  const handleBeurteilungQuickPromptApply = (prompt) => {
    const promptText = prompt?.text;
    const nextPrompt = String(promptText || "").trim();
    if (!nextPrompt) return;
    const promptTarget = prompt?.target === "assistant" ? "assistant" : "report";
    const hasBeurteilung = !!fields.beurteilung?.trim();

    if (promptTarget === "assistant") {
      const basePrompt = String(aiBeurteilungPrompt || "").trim();
      const combinedPrompt = basePrompt ? `${basePrompt}\n\n${nextPrompt}` : nextPrompt;
      setAiBeurteilungPrompt(combinedPrompt);
      if (!hasBeurteilung) {
        showStatus("Prompt gesetzt. Bitte zuerst eine Beurteilung eingeben oder generieren, dann AI-Assistent starten.", true);
        return;
      }
      runAiBeurteilungAssistant(combinedPrompt);
      return;
    }

    setAiBeurteilungPrompt(nextPrompt);
    if (!hasBeurteilung) {
      showStatus("Prompt gesetzt. Bitte zuerst eine Beurteilung eingeben oder generieren, dann AI-Assistent starten.", true);
      return;
    }
    runAiBeurteilungAssistant(nextPrompt);
  };

  const {
    directPrompts: befundDirectPrompts,
    promptOnPromptPrompts: befundPromptOnPromptPrompts,
  } = useMemo(() => splitPromptsByTarget(aiQuickPrompts), [aiQuickPrompts]);
  const {
    directPrompts: beurteilungDirectPrompts,
    promptOnPromptPrompts: beurteilungPromptOnPromptPrompts,
  } = useMemo(() => splitPromptsByTarget(aiBeurteilungQuickPrompts), [aiBeurteilungQuickPrompts]);

  const miniActionButtonStyle = (variant, disabled) => {
    if (variant === "redo") {
      return {
        background: dark ? "#0f2e1a" : "#edfff2",
        color: dark ? "#68d391" : "#1f7a3d",
        border: `1px solid ${dark ? "#68d391" : "#86efac"}`,
        borderRadius: "8px",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: "6px 12px",
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      };
    }

    if (variant === "copy") {
      return {
        background: dark ? "#2d2a00" : "#fffbea",
        color: dark ? "#f6d860" : "#92610a",
        border: `1px solid ${dark ? "#f6d860" : "#f6d860"}`,
        borderRadius: "8px",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: "6px 12px",
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      };
    }

    return {
      background: dark ? "#2d1515" : "#fff0f0",
      color: dark ? "#fc8181" : "#cc3333",
      border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`,
      borderRadius: "8px",
      cursor: disabled ? "not-allowed" : "pointer",
      padding: "6px 12px",
      fontSize: 12,
      opacity: disabled ? 0.4 : 1,
    };
  };

  const renderCopyUndoButtons = ({ value, undoCount, redoCount, onCopy, onUndo, onRedo }) => {
    const copyDisabled = !value;
    const undoDisabled = !undoCount;
    const redoDisabled = !redoCount;

    return (
      <>
        <button
          onClick={onUndo}
          disabled={undoDisabled}
          style={miniActionButtonStyle("undo", undoDisabled)}
          title="Rückgängig"
        >
          ↶
        </button>
        <button
          onClick={onRedo}
          disabled={redoDisabled}
          style={miniActionButtonStyle("redo", redoDisabled)}
          title="Vorwärts"
        >
          ↷
        </button>
        <button
          onClick={onCopy}
          disabled={copyDisabled}
          style={miniActionButtonStyle("copy", copyDisabled)}
          title="Kopieren"
        >
          ⎘
        </button>
      </>
    );
  };

  const aiActionButtonStyle = (variant, disabled = false) => {
    if (variant === "info") {
      return {
        background: dark ? "#0d2137" : "#ebf8ff",
        color: dark ? "#63b3ed" : "#2b6cb0",
        border: `1px solid ${dark ? "#63b3ed" : "#90cdf4"}`,
        borderRadius: "8px",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: "6px 10px",
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      };
    }

    return {
      background: dark ? "#2d1515" : "#fff0f0",
      color: dark ? "#fc8181" : "#cc3333",
      border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`,
      borderRadius: "8px",
      cursor: disabled ? "not-allowed" : "pointer",
      padding: "6px 10px",
      fontSize: 12,
      opacity: disabled ? 0.4 : 1,
    };
  };

  const quickPromptChipButtonStyle = (variant) => {
    if (variant === "main") {
      return {
        background: "transparent",
        color: c.textSecondary,
        border: "none",
        cursor: "pointer",
        padding: "5px 10px",
        fontSize: 11,
        textAlign: "left",
        maxWidth: 220,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
    }

    return {
      background: "transparent",
      color: variant === "edit" ? (dark ? "#fbbf24" : "#d97706") : (dark ? "#fc8181" : "#cc3333"),
      border: "none",
      borderLeft: `1px solid ${c.border}`,
      cursor: "pointer",
      padding: "5px 8px",
      fontSize: 11,
    };
  };

  const quickPromptEditorActionButtonStyle = (variant) => {
    if (variant === "save") {
      return {
        background: dark ? "#0d2d1a" : "#f0fff4",
        color: dark ? "#48bb78" : "#276749",
        border: `1px solid ${dark ? "#48bb78" : "#9ae6b4"}`,
        borderRadius: "8px",
        cursor: "pointer",
        padding: "5px 9px",
        fontSize: 12,
      };
    }

    if (variant === "delete") {
      return {
        background: dark ? "#2d1515" : "#fff0f0",
        color: dark ? "#fc8181" : "#cc3333",
        border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`,
        borderRadius: "8px",
        cursor: "pointer",
        padding: "5px 9px",
        fontSize: 12,
      };
    }

    return {
      background: "transparent",
      color: c.textSecondary,
      border: `1px solid ${c.border}`,
      borderRadius: "8px",
      cursor: "pointer",
      padding: "5px 9px",
      fontSize: 12,
    };
  };

  const sectionHeaderRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 7,
  };

  const panelCardStyle = {
    background: c.bgSecondary,
    borderRadius: "12px",
    border: `1px solid ${c.border}`,
  };

  const editorUtilityRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 8,
  };

  const historySaveButtonStyle = {
    background: dark ? "#0d2137" : "#ebf8ff",
    color: dark ? "#63b3ed" : "#2b6cb0",
    border: `1px solid ${dark ? "#63b3ed" : "#90cdf4"}`,
    borderRadius: "8px",
    cursor: "pointer",
    padding: "6px 12px",
    fontSize: 12,
  };

  const editorColumnStyle = {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  };

  return (
    <div
      style={{ padding: "14px 18px", maxWidth: 1640, margin: "0 auto", boxSizing: "border-box", position: "relative" }}
      onFocusCapture={handleEditorFocusCapture}
    >
      <style>{`
        .editor-top-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 12px;
        }

        .editor-meta-grid {
          display: grid;
          grid-template-columns: repeat(3, max-content);
          gap: 12px;
          margin-bottom: 14px;
          align-items: start;
          justify-content: start;
        }

        .editor-main-layout {
          display: grid;
          grid-template-columns: minmax(0, 2.2fr) minmax(340px, 0.95fr);
          gap: 14px;
          align-items: stretch;
        }

        .editor-ai-panel {
          min-width: 0;
          width: 100%;
          max-width: none;
        }

        .editor-main-layout > .editor-ai-panel,
        .editor-beurteilung-layout > .editor-ai-panel {
          margin-top: 0;
        }

        .editor-beurteilung-layout {
          display: grid;
          grid-template-columns: minmax(0, 2.2fr) minmax(340px, 0.95fr);
          gap: 14px;
          align-items: stretch;
        }

        @media (max-width: 1520px) {
          .editor-main-layout {
            grid-template-columns: minmax(0, 1.95fr) minmax(300px, 0.85fr);
          }

          .editor-beurteilung-layout {
            grid-template-columns: minmax(0, 1.95fr) minmax(300px, 0.85fr);
          }
        }

        @media (max-width: 1040px) {
          .editor-meta-grid {
            grid-template-columns: repeat(2, max-content);
          }
        }

        @media (max-width: 1400px) {
          .editor-main-layout {
            grid-template-columns: minmax(0, 1fr);
          }

          .editor-beurteilung-layout {
            grid-template-columns: minmax(0, 1fr);
          }

          .editor-main-layout > .editor-ai-panel,
          .editor-beurteilung-layout > .editor-ai-panel {
            margin-top: 0;
          }
        }

        @media (max-width: 760px) {
          .editor-top-grid,
          .editor-meta-grid {
            grid-template-columns: minmax(0, 1fr);
          }
        }
      `}</style>
      {dataWarningPopup.visible && (
        <div
          style={{
            position: "fixed",
            top: dataWarningPopup.top,
            left: dataWarningPopup.left ?? "50%",
            transform: "translate(-50%, -100%)",
            zIndex: 1600,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: ".01em",
            color: dark ? "#fbd38d" : "#7c4700",
            background: dark ? "rgba(45,32,16,.95)" : "rgba(255,247,230,.97)",
            border: `1px solid ${dark ? "#f6ad55" : "#fcd34d"}`,
            borderRadius: 999,
            padding: "7px 12px",
            boxShadow: dark ? "0 10px 30px rgba(0,0,0,.35)" : "0 8px 22px rgba(120,90,30,.18)",
            pointerEvents: "none",
          }}
        >
          Keine personenbezogenen Daten eingeben
        </div>
      )}
      <div className="editor-top-grid">
        <div>
          <span style={lbl}>Klinische Angaben</span>
          <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
            <textarea
              ref={klinischeTextareaRef}
              value={fields.klinischeAngaben}
              onChange={(e) => updateKlinischeAngaben(e.target.value)}
              onPaste={(e) => handlePasteNormalizeDelimiters(e, updateKlinischeAngaben, fields.klinischeAngaben)}
              onInput={(e) => autoResizeTextarea(e.currentTarget, 5)}
              rows={2}
              placeholder="Vorgeschichte, Symptome, Klinik..."
              style={{ ...inp, flex: 1, resize: "none", overflowY: "hidden", minHeight: 44, lineHeight: 1.5 }}
            />
          </div>
        </div>
        <div>
          <span style={lbl}>Fragestellung</span>
          <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
            <textarea
              ref={fragestellungTextareaRef}
              value={fields.fragestellung}
              onChange={(e) => updateFragestellung(e.target.value)}
              onPaste={(e) => handlePasteNormalizeDelimiters(e, updateFragestellung, fields.fragestellung)}
              onInput={(e) => autoResizeTextarea(e.currentTarget, 5)}
              rows={2}
              placeholder="Was soll beantwortet werden?"
              style={{ ...inp, flex: 1, resize: "none", overflowY: "hidden", minHeight: 44, lineHeight: 1.5 }}
            />
          </div>
        </div>
      </div>

      <div className="editor-meta-grid">
        <InlineDropdown
          label="Modalität"
          value={fields.modalitat}
          open={modOpen}
          dark={dark}
          onToggle={() => {
            setModOpen((o) => !o);
            setRegionOpen(false);
            setGeschlechtOpen(false);
          }}
          onClear={() => selectModality(fields.modalitat)}
          triggerBg={fields.modalitat ? modColor(fields.modalitat).bg : undefined}
          triggerColor={fields.modalitat ? modColor(fields.modalitat).color : undefined}
        >
          {modalityTree && !modDropdownGroup
            ? modalityTree.map((node, i) => {
                const isActive = fields.modalitat && fields.modalitat.split(" ")[0] === node.group;
                const mc = modColor(node.group);
                return (
                  <div
                    key={node.group}
                    onClick={() => {
                      if (node.single) {
                        selectModality(node.items[0]);
                      } else {
                        setModDropdownGroup(node.group);
                      }
                    }}
                    style={{ padding: "10px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", background: isActive ? mc.bg : c.bg, color: isActive ? mc.color : c.text, borderBottom: i < modalityTree.length - 1 ? `1px solid ${c.border}` : "none", fontWeight: isActive ? 600 : 400 }}
                  >
                    <span>{node.group}</span>
                    {!node.single && <span style={{ fontSize: 14, opacity: 0.55 }}>›</span>}
                  </div>
                );
              })
            : modalityTree && modDropdownGroup
            ? (() => {
                const node = modalityTree.find((n) => n.group === modDropdownGroup);
                return (
                  <>
                    <div
                      onClick={() => setModDropdownGroup("")}
                      style={{ padding: "8px 14px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: c.textSecondary, borderBottom: `1px solid ${c.border}`, background: c.bg, fontWeight: 500 }}
                    >
                      <span style={{ fontSize: 14 }}>‹</span>
                      <span>{modDropdownGroup}</span>
                    </div>
                    {(node?.items || []).map((itemName, i, arr) => {
                      const mc = modColor(itemName);
                      return (
                        <div
                          key={itemName}
                          onClick={() => selectModality(itemName)}
                          style={{ padding: "10px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", background: fields.modalitat === itemName ? mc.bg : c.bg, color: fields.modalitat === itemName ? mc.color : c.text, borderBottom: i < arr.length - 1 ? `1px solid ${c.border}` : "none", fontWeight: fields.modalitat === itemName ? 600 : 400 }}
                        >
                          <span>{itemName}</span>
                          {fields.modalitat === itemName && <span style={{ fontSize: 11 }}>✓</span>}
                        </div>
                      );
                    })}
                  </>
                );
              })()
            : modalities.map((m) => {
                const mc = modColor(m.name);
                return (
                  <div key={m.name} onClick={() => selectModality(m.name)} style={{ padding: "10px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", background: fields.modalitat === m.name ? mc.bg : c.bg, color: fields.modalitat === m.name ? mc.color : c.text, borderBottom: m !== modalities[modalities.length - 1] ? `1px solid ${c.border}` : "none", fontWeight: fields.modalitat === m.name ? 600 : 400 }}>
                    <span>{m.name}</span>
                  </div>
                );
              })}
        </InlineDropdown>

        <InlineDropdown
          label="Körperregion"
          value={regionTriggerValue}
          open={regionOpen}
          dark={dark}
          onToggle={() => {
            setRegionOpen((o) => !o);
            setModOpen(false);
            setGeschlechtOpen(false);
          }}
          onClear={() => selectRegion(fields.region)}
        >
          {regionTree && !regionDropdownSection
            ? (
              <>
                {regionTree.sections.map((sec, i) => {
                  const isActive = regionTree.sections.find((s) =>
                    s.items.some((it) => it.value === fields.region)
                  )?.name === sec.name;
                  return (
                    <div
                      key={sec.name}
                      onClick={() => {
                        setRegionDropdownSection(sec.name);
                        setRegionDropdownCategory("");
                      }}
                      style={{ padding: "10px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", background: isActive ? (c.bgSecondary) : c.bg, color: isActive ? c.text : c.text, borderBottom: `1px solid ${c.border}`, fontWeight: isActive ? 600 : 400 }}
                    >
                      <span>{sec.name}</span>
                      <span style={{ fontSize: 14, opacity: 0.55 }}>›</span>
                    </div>
                  );
                })}
                {regionTree.direct.map((option, i) => (
                  <DropItem
                    key={option.value}
                    onClick={() => {
                      selectRegion(option.value);
                      setRegionDropdownSection("");
                      setRegionDropdownCategory("");
                    }}
                    selected={fields.region === option.value}
                    last={i === regionTree.direct.length - 1}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {option.label}
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.success, display: "inline-block", flexShrink: 0 }} />
                    </span>
                  </DropItem>
                ))}
              </>
            )
            : regionTree && regionDropdownSection
            ? (() => {
                const sec = regionTree.sections.find((s) => s.name === regionDropdownSection);
                if (sec?.hasNestedCategories && !regionDropdownCategory) {
                  return (
                    <>
                      <div
                        onClick={() => {
                          setRegionDropdownSection("");
                          setRegionDropdownCategory("");
                        }}
                        style={{ padding: "8px 14px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: c.textSecondary, borderBottom: `1px solid ${c.border}`, background: c.bg, fontWeight: 500 }}
                      >
                        <span style={{ fontSize: 14 }}>‹</span>
                        <span>{regionDropdownSection}</span>
                      </div>
                      {(sec.categories || []).map((category, i, arr) => {
                        const isActive = category.items.some((it) => it.value === fields.region);
                        return (
                          <div
                            key={category.name}
                            onClick={() => setRegionDropdownCategory(category.name)}
                            style={{ padding: "10px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", background: isActive ? c.bgSecondary : c.bg, color: c.text, borderBottom: i === arr.length - 1 ? "none" : `1px solid ${c.border}`, fontWeight: isActive ? 600 : 400 }}
                          >
                            <span>{category.name}</span>
                            <span style={{ fontSize: 14, opacity: 0.55 }}>›</span>
                          </div>
                        );
                      })}
                    </>
                  );
                }
                const visibleItems = sec?.hasNestedCategories
                  ? sec.categories?.find((category) => category.name === regionDropdownCategory)?.items || []
                  : sec?.items || [];
                return (
                  <>
                    <div
                      onClick={() => {
                        if (regionDropdownCategory) {
                          setRegionDropdownCategory("");
                          return;
                        }
                        setRegionDropdownSection("");
                      }}
                      style={{ padding: "8px 14px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: c.textSecondary, borderBottom: `1px solid ${c.border}`, background: c.bg, fontWeight: 500 }}
                    >
                      <span style={{ fontSize: 14 }}>‹</span>
                      <span>{regionDropdownCategory || regionDropdownSection}</span>
                    </div>
                    {visibleItems.map((option, i, arr) => (
                      <DropItem
                        key={option.value}
                        onClick={() => {
                          selectRegion(option.value);
                          setRegionDropdownSection("");
                          setRegionDropdownCategory("");
                        }}
                        selected={fields.region === option.value}
                        last={i === arr.length - 1}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {option.label}
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.success, display: "inline-block", flexShrink: 0 }} />
                        </span>
                      </DropItem>
                    ))}
                  </>
                );
              })()
            : regionOptions.map((option, i) => (
            <DropItem key={option.value} onClick={() => selectRegion(option.value)} selected={fields.region === option.value} last={i === regionOptions.length - 1}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {option.label}
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.success, display: "inline-block", flexShrink: 0 }} />
              </span>
            </DropItem>
          ))}
        </InlineDropdown>

        {fields.modalitat && fields.modalitat.split(" ")[0] === "MRT" && selectedRegionDisplayName && selectedRegionDisplayName.startsWith("Herz") && (
          <InlineDropdown
            label="Volumetrie"
            dark={dark}
            value={fields.geschlecht ? (fields.geschlecht === "Mann" ? "♂ Mann" : "♀ Frau") : ""}
            open={geschlechtOpen}
            onToggle={() => {
              setGeschlechtOpen((o) => !o);
              setModOpen(false);
              setRegionOpen(false);
            }}
            onClear={() => selectGeschlecht(fields.geschlecht)}
          >
            {vorlagenForMod("Volumetrie").map((prompt, i, arr) => (
              <DropItem key={prompt.id} onClick={() => selectGeschlecht(prompt.name)} selected={fields.geschlecht === prompt.name} last={i === arr.length - 1}>
                {prompt.name === "Mann" ? "♂ Mann" : prompt.name === "Frau" ? "♀ Frau" : prompt.name}
              </DropItem>
            ))}
          </InlineDropdown>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="editor-main-layout">
          <div style={{ ...editorColumnStyle, ...panelCardStyle, padding: 14, boxSizing: "border-box" }}>
            <div style={sectionHeaderRowStyle}>
              <span style={lbl}>Befund</span>
            </div>
            <RichTextEditor
              value={fields.befund}
              onChange={(value) => updateField("befund", value)}
              onSelectionChange={updateAiBefundSelection}
              placeholder="Modalität, Region und Geschlecht wählen für automatische Vorlage..."
              minHeight={500}
              c={c}
              dark={dark}
              ariaLabel="Befund"
            />
            <div style={editorUtilityRowStyle}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  onClick={saveCurrentToHistory}
                  style={historySaveButtonStyle}
                >
                  + Im Verlauf speichern
                </button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                {renderCopyUndoButtons({
                  value: richTextToPlainText(fields.befund),
                  undoCount: fieldHistory.befund.length,
                  redoCount: fieldFuture.befund.length,
                  onCopy: () => copyText(fields.befund),
                  onUndo: () => undoField("befund"),
                  onRedo: () => redoField("befund"),
                })}
              </div>
            </div>
          </div>

          <div className="editor-ai-panel" style={{ ...panelCardStyle, display: "flex", flexDirection: "column", gap: 12, padding: 14, boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={lbl}>AI-Assistent für Befund</span>
            </div>

            <div>
              <textarea
                value={aiBefundPrompt}
                onChange={(e) => setAiBefundPrompt(e.target.value)}
                rows={15}
                placeholder="z.B. Formuliere den Befund kürzer und präziser, ohne Inhalte zu verlieren."
                style={{ ...inp, width: "100%", resize: "vertical", minHeight: 340, boxSizing: "border-box", lineHeight: 1.6 }}
              />
              <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => setAiBefundPrompt("")}
                  disabled={!aiBefundPrompt}
                  style={aiActionButtonStyle("danger", !aiBefundPrompt)}
                >
                  Eingabe löschen
                </button>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <QuickPromptChipGroup
                c={c}
                title="Direkt auf Befund"
                prompts={befundDirectPrompts}
                onApply={handleQuickPromptApply}
                onEdit={startQuickPromptEdit}
                onDelete={handleQuickPromptDeleteFromChip}
                canDelete={canDeleteAiPrompts}
                quickPromptChipButtonStyle={quickPromptChipButtonStyle}
                titleSuffix={`\n\n${QUICK_PROMPT_SELECTION_HINT}`}
              />
              <QuickPromptChipGroup
                c={c}
                title="Prompt auf Prompt (AI-Feld)"
                prompts={befundPromptOnPromptPrompts}
                onApply={handleQuickPromptApply}
                onEdit={startQuickPromptEdit}
                onDelete={handleQuickPromptDeleteFromChip}
                canDelete={canDeleteAiPrompts}
                quickPromptChipButtonStyle={quickPromptChipButtonStyle}
                titleMarginTop={2}
              />
              {canDeleteAiPrompts && (
                <button
                  onClick={startQuickPromptCreate}
                  style={{
                    background: c.bg,
                    color: dark ? "#63b3ed" : "#2b6cb0",
                    border: `1px dashed ${dark ? "#63b3ed" : "#90cdf4"}`,
                    borderRadius: 999,
                    cursor: "pointer",
                    padding: "5px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                  title="Neuen Prompt erstellen"
                >
                  + Neuer Prompt
                </button>
              )}
            </div>

            {quickPromptEditorOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 10, background: c.bg, border: `1px solid ${c.border}`, borderRadius: "10px", position: "relative" }}>
                <button
                  onClick={resetQuickPromptEditor}
                  title="Schließen"
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "transparent",
                    color: c.textSecondary,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                    padding: "2px 4px",
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: c.textSecondary }}>
                  Prompt bearbeiten
                </div>
                <input
                  type="text"
                  value={quickPromptTitleInput}
                  onChange={(e) => setQuickPromptTitleInput(e.target.value)}
                  placeholder="Name des Prompts"
                  style={{ ...inp, width: "100%", boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button
                    onClick={() => setQuickPromptTargetInput("report")}
                    style={{
                      ...quickPromptEditorActionButtonStyle("cancel"),
                      background: quickPromptTargetInput === "report" ? (dark ? "#0d2137" : "#ebf8ff") : "transparent",
                      color: quickPromptTargetInput === "report" ? (dark ? "#63b3ed" : "#2b6cb0") : c.textSecondary,
                    }}
                  >
                    Direkt auf Befund
                  </button>
                  <button
                    onClick={() => setQuickPromptTargetInput("assistant")}
                    style={{
                      ...quickPromptEditorActionButtonStyle("cancel"),
                      background: quickPromptTargetInput === "assistant" ? (dark ? "#2d2010" : "#fffbeb") : "transparent",
                      color: quickPromptTargetInput === "assistant" ? (dark ? "#f6ad55" : "#b45309") : c.textSecondary,
                    }}
                  >
                    Prompt auf Prompt
                  </button>
                </div>
                <textarea
                  value={quickPromptTextInput}
                  onChange={(e) => setQuickPromptTextInput(e.target.value)}
                  rows={6}
                  placeholder="Prompt-Text für den AI-Assistenten"
                  style={{ ...inp, width: "100%", resize: "vertical", minHeight: 140, boxSizing: "border-box", lineHeight: 1.6 }}
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canDeleteAiPrompts && (
                    <button
                      onClick={handleQuickPromptSave}
                      style={quickPromptEditorActionButtonStyle("save")}
                      title="Speichern"
                    >
                      Speichern
                    </button>
                  )}
                  {editingQuickPromptId && (
                    <button
                      onClick={handleQuickPromptDelete}
                      disabled={!canDeleteAiPrompts}
                      style={{
                        ...quickPromptEditorActionButtonStyle("delete"),
                        cursor: canDeleteAiPrompts ? "pointer" : "not-allowed",
                        opacity: canDeleteAiPrompts ? 1 : 0.4,
                      }}
                      title={canDeleteAiPrompts ? "Löschen" : "Nur nach Anmeldung löschbar"}
                    >
                      Löschen
                    </button>
                  )}
                  <button
                    onClick={resetQuickPromptEditor}
                    style={quickPromptEditorActionButtonStyle("cancel")}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn onClick={() => runAiBefundAssistant("full")} disabled={isAiBefundLoading || !fields.befund || !aiBefundPrompt.trim()} primary>
                {isAiBefundLoading ? (
                  <>
                    <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin .7s linear infinite", display: "inline-block" }} />
                    Analysiere...
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v6m0 6v6m9-9h-6M9 12H3" /><path d="M18.364 5.636l-4.243 4.243M9.879 14.121l-4.243 4.243M18.364 18.364l-4.243-4.243M9.879 9.879 5.636 5.636" /></svg>
                    Direkt anwenden
                  </>
                )}
              </Btn>
              <button
                onClick={() => runAiBefundAssistant("selection")}
                disabled={isAiBefundLoading || !aiBefundPrompt.trim() || !aiBefundSelection.text}
                style={aiActionButtonStyle(
                  "info",
                  isAiBefundLoading || !aiBefundPrompt.trim() || !aiBefundSelection.text
                )}
                title={aiBefundSelection.text ? "Analysiert nur den markierten Abschnitt im Befund" : "Bitte zuerst einen Abschnitt im Befund markieren"}
              >
                Auswahl direkt anwenden
              </button>
            </div>
            {aiBefundSelection.text && (
              <div style={{ fontSize: 11, color: c.textSecondary, lineHeight: 1.5 }}>
                Auswahl aktiv: {aiBefundSelection.text.length} Zeichen markiert.
              </div>
            )}
          </div>
        </div>

        <div className="editor-beurteilung-layout">
          <div style={{ ...editorColumnStyle, ...panelCardStyle, padding: 14, boxSizing: "border-box" }}>
            <div style={sectionHeaderRowStyle}>
              <span style={lbl}>Beurteilung</span>
            </div>
            <RichTextEditor
              value={fields.beurteilung}
              onChange={(value) => updateField("beurteilung", value)}
              placeholder="KI-generierte Beurteilung erscheint hier..."
              minHeight={220}
              c={c}
              dark={dark}
              ariaLabel="Beurteilung"
            />
            <div style={editorUtilityRowStyle}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <Btn onClick={() => void generateBeurteilung()} disabled={isGenerating || !hasRichTextContent(fields.befund)} primary>
                  {isGenerating ? (
                    <>
                      <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin .7s linear infinite", display: "inline-block" }} />
                      Generiere...
                    </>
                  ) : (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                      Beurteilung generieren
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowGeneratePromptEditor(true);
                        }}
                        style={{ marginLeft: 8, cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                        title="Prompt bearbeiten"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                      </span>
                    </>
                  )}
                </Btn>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {[
                    {
                      value: "ultrakurz",
                      label: "Ultrakurz",
                      help: "1 Satz, nur wegweisende Kernaussage; keine Aufzählung unauffälliger Details.",
                    },
                    {
                      value: "standard",
                      label: "Standard",
                      help: "2 prägnante Sätze mit direkter Antwort auf die Fragestellung.",
                    },
                    {
                      value: "detailliert",
                      label: "Detailliert",
                      help: "Bis zu 4 kurze Sätze, mehr Kontext ohne redundante Negativlisten.",
                    },
                  ].map((profile) => {
                    const active = beurteilungStyleProfile === profile.value;
                    return (
                      <button
                        key={profile.value}
                        onClick={() => setBeurteilungStyleProfile(profile.value)}
                        style={{
                          background: active ? (dark ? "#0d2137" : "#ebf8ff") : c.bg,
                          color: active ? (dark ? "#63b3ed" : "#2b6cb0") : c.textSecondary,
                          border: `1px solid ${active ? (dark ? "#63b3ed" : "#90cdf4") : c.border}`,
                          borderRadius: "999px",
                          cursor: "pointer",
                          padding: "5px 10px",
                          fontSize: 11,
                          fontWeight: active ? 700 : 600,
                        }}
                        title={profile.help}
                      >
                        {profile.label}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={saveCurrentToHistory}
                  style={historySaveButtonStyle}
                >
                  + Im Verlauf speichern
                </button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                {renderCopyUndoButtons({
                  value: fields.beurteilung,
                  undoCount: fieldHistory.beurteilung.length,
                  redoCount: fieldFuture.beurteilung.length,
                  onCopy: () => copyText(fields.beurteilung),
                  onUndo: () => undoField("beurteilung"),
                  onRedo: () => redoField("beurteilung"),
                })}
              </div>
            </div>
            {lastAutosaveAt && (
              <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                <span style={{ fontSize: 11, color: c.textSecondary }}>
                  Autospeicherung: {new Date(lastAutosaveAt).toLocaleString()}
                </span>
              </div>
            )}
            {showGeneratePromptEditor && (
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.35)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12, padding: 24, minWidth: 340, maxWidth: 420, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: c.textSecondary, marginBottom: 10 }}>
                    Prompt für "Beurteilung generieren"
                  </div>
                  <textarea
                    value={beurteilungGeneratePrompt}
                    onChange={(e) => setBeurteilungGeneratePrompt(e.target.value)}
                    rows={5}
                    placeholder="Anweisung für die automatische Beurteilung..."
                    style={{ ...inp, width: "100%", resize: "vertical", boxSizing: "border-box", lineHeight: 1.6, marginBottom: 16 }}
                  />
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button
                      onClick={() => setShowGeneratePromptEditor(false)}
                      style={quickPromptEditorActionButtonStyle("cancel")}
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={() => {
                        onSaveBeurteilungGeneratePrompt();
                        setShowGeneratePromptEditor(false);
                      }}
                      style={quickPromptEditorActionButtonStyle("save")}
                    >
                      💾 Speichern
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="editor-ai-panel" style={{ ...panelCardStyle, display: "flex", flexDirection: "column", gap: 12, padding: 14, boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={lbl}>AI-Assistent für Beurteilung</span>
            </div>

            <div>
              <textarea
                value={aiBeurteilungPrompt}
                onChange={(e) => setAiBeurteilungPrompt(e.target.value)}
                rows={12}
                placeholder="z.B. Formuliere die Beurteilung präziser und beantworte die Fragestellung direkter."
                style={{ ...inp, width: "100%", resize: "vertical", minHeight: 280, boxSizing: "border-box", lineHeight: 1.6 }}
              />
              <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => setAiBeurteilungPrompt("")}
                  disabled={!aiBeurteilungPrompt}
                  style={aiActionButtonStyle("danger", !aiBeurteilungPrompt)}
                >
                  Eingabe löschen
                </button>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <QuickPromptChipGroup
                c={c}
                title="Direkt auf Beurteilung"
                prompts={beurteilungDirectPrompts}
                onApply={handleBeurteilungQuickPromptApply}
                onEdit={startBeurteilungQuickPromptEdit}
                onDelete={handleBeurteilungQuickPromptDeleteFromChip}
                canDelete={canDeleteAiPrompts}
                quickPromptChipButtonStyle={quickPromptChipButtonStyle}
              />
              <QuickPromptChipGroup
                c={c}
                title="Prompt auf Prompt (AI-Feld)"
                prompts={beurteilungPromptOnPromptPrompts}
                onApply={handleBeurteilungQuickPromptApply}
                onEdit={startBeurteilungQuickPromptEdit}
                onDelete={handleBeurteilungQuickPromptDeleteFromChip}
                canDelete={canDeleteAiPrompts}
                quickPromptChipButtonStyle={quickPromptChipButtonStyle}
                titleMarginTop={2}
              />
              {canDeleteAiPrompts && (
                <button
                  onClick={startBeurteilungQuickPromptCreate}
                  style={{
                    background: c.bg,
                    color: dark ? "#63b3ed" : "#2b6cb0",
                    border: `1px dashed ${dark ? "#63b3ed" : "#90cdf4"}`,
                    borderRadius: 999,
                    cursor: "pointer",
                    padding: "5px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                  title="Neuen Prompt erstellen"
                >
                  + Neuer Prompt
                </button>
              )}
            </div>

            {beurteilungQuickPromptEditorOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 10, background: c.bg, border: `1px solid ${c.border}`, borderRadius: "10px", position: "relative" }}>
                <button
                  onClick={resetBeurteilungQuickPromptEditor}
                  title="Schließen"
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "transparent",
                    color: c.textSecondary,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                    padding: "2px 4px",
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: c.textSecondary }}>
                  Prompt bearbeiten
                </div>
                <input
                  type="text"
                  value={beurteilungQuickPromptTitleInput}
                  onChange={(e) => setBeurteilungQuickPromptTitleInput(e.target.value)}
                  placeholder="Name des Prompts"
                  style={{ ...inp, width: "100%", boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button
                    onClick={() => setBeurteilungQuickPromptTargetInput("report")}
                    style={{
                      ...quickPromptEditorActionButtonStyle("cancel"),
                      background: beurteilungQuickPromptTargetInput === "report" ? (dark ? "#0d2137" : "#ebf8ff") : "transparent",
                      color: beurteilungQuickPromptTargetInput === "report" ? (dark ? "#63b3ed" : "#2b6cb0") : c.textSecondary,
                    }}
                  >
                    Direkt auf Beurteilung
                  </button>
                  <button
                    onClick={() => setBeurteilungQuickPromptTargetInput("assistant")}
                    style={{
                      ...quickPromptEditorActionButtonStyle("cancel"),
                      background: beurteilungQuickPromptTargetInput === "assistant" ? (dark ? "#2d2010" : "#fffbeb") : "transparent",
                      color: beurteilungQuickPromptTargetInput === "assistant" ? (dark ? "#f6ad55" : "#b45309") : c.textSecondary,
                    }}
                  >
                    Prompt auf Prompt
                  </button>
                </div>
                <textarea
                  value={beurteilungQuickPromptTextInput}
                  onChange={(e) => setBeurteilungQuickPromptTextInput(e.target.value)}
                  rows={6}
                  placeholder="Prompt-Text für den AI-Assistenten"
                  style={{ ...inp, width: "100%", resize: "vertical", minHeight: 140, boxSizing: "border-box", lineHeight: 1.6 }}
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canDeleteAiPrompts && (
                    <button
                      onClick={handleBeurteilungQuickPromptSave}
                      style={quickPromptEditorActionButtonStyle("save")}
                      title="Speichern"
                    >
                      Speichern
                    </button>
                  )}
                  {editingBeurteilungQuickPromptId && (
                    <button
                      onClick={handleBeurteilungQuickPromptDelete}
                      disabled={!canDeleteAiPrompts}
                      style={{
                        ...quickPromptEditorActionButtonStyle("delete"),
                        cursor: canDeleteAiPrompts ? "pointer" : "not-allowed",
                        opacity: canDeleteAiPrompts ? 1 : 0.4,
                      }}
                      title={canDeleteAiPrompts ? "Löschen" : "Nur nach Anmeldung löschbar"}
                    >
                      Löschen
                    </button>
                  )}
                  <button
                    onClick={resetBeurteilungQuickPromptEditor}
                    style={quickPromptEditorActionButtonStyle("cancel")}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn onClick={() => runAiBeurteilungAssistant()} disabled={isAiBeurteilungLoading || !fields.beurteilung || !aiBeurteilungPrompt.trim()} primary>
                {isAiBeurteilungLoading ? (
                  <>
                    <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin .7s linear infinite", display: "inline-block" }} />
                    Analysiere...
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v6m0 6v6m9-9h-6M9 12H3" /><path d="M18.364 5.636l-4.243 4.243M9.879 14.121l-4.243 4.243M18.364 18.364l-4.243-4.243M9.879 9.879 5.636 5.636" /></svg>
                    Direkt anwenden
                  </>
                )}
              </Btn>
            </div>
          </div>
        </div>
      </div>

      <div style={{ ...panelCardStyle, marginTop: 14, padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={lbl}>Befundverlauf (Sitzungsspeicherung)</span>
          <button
            onClick={clearHistory}
            disabled={!reportHistory.length}
            style={{
              background: dark ? "#2d1515" : "#fff0f0",
              color: dark ? "#fc8181" : "#cc3333",
              border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`,
              borderRadius: "8px",
              cursor: reportHistory.length ? "pointer" : "not-allowed",
              padding: "4px 10px",
              fontSize: 11,
              opacity: reportHistory.length ? 1 : 0.4,
            }}
          >
            Verlauf löschen
          </button>
        </div>

        {reportHistory.length === 0 ? (
          <div style={{ fontSize: 12, color: c.textSecondary }}>Noch keine gespeicherten Befunde.</div>
        ) : (
          <div style={{ display: "grid", gap: 6, maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
            {reportHistory.map((item) => (
              <div key={item.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: c.bg, border: `1px solid ${c.border}`, borderRadius: "8px", padding: "7px 9px" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title}</div>
                  <div style={{ fontSize: 11, color: c.textSecondary }}>{new Date(item.savedAt).toLocaleString()}</div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => restoreFromHistory(item.id)}
                    style={{ background: dark ? "#0d2d1a" : "#f0fff4", color: dark ? "#48bb78" : "#276749", border: `1px solid ${dark ? "#48bb78" : "#9ae6b4"}`, borderRadius: "8px", cursor: "pointer", padding: "4px 8px", fontSize: 11 }}
                  >
                    Laden
                  </button>
                  <button
                    onClick={() => deleteHistoryEntry(item.id)}
                    style={{ background: dark ? "#2d1515" : "#fff0f0", color: dark ? "#fc8181" : "#cc3333", border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`, borderRadius: "8px", cursor: "pointer", padding: "4px 8px", fontSize: 11 }}
                  >
                    Löschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
        <button onClick={resetApp} style={{ background: dark ? "#2d1515" : "#fff0f0", color: dark ? "#fc8181" : "#cc3333", border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`, borderRadius: "8px", cursor: "pointer", padding: "6px 12px", fontSize: 12 }}>
          ↺ Zurücksetzen
        </button>
      </div>

    </div>
  );
}

export default memo(EditorTab);
